import { XIcon, type LucideIcon } from "~/icons/solar-lucide";
import { WorkStepLifecycleIndicator } from "./WorkStepLifecycleIndicator";
import { forwardRef, useState } from "react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";

/**
 * A single "connect an app" card on the first-win landing.
 *
 * Glass frame (conic highlight + frosted fill). App identity lives in the
 * logo, not a color stripe. The Connect CTA is the brand blue; everything
 * else stays neutral.
 */
export interface ConnectAppCardModel {
  /** Any Composio toolkit slug — the recommended grid isn't limited to the five. */
  readonly toolkit: string;
  readonly name: string;
  readonly icon: LucideIcon;
  /** Fallback tile fill when the remote logo fails to load. */
  readonly brand: string;
  /** Glyph color drawn on top of the brand-colored fallback tile. */
  readonly glyph: string;
}

interface ConnectAppCardProps {
  readonly model: ConnectAppCardModel;
  readonly connected: boolean;
  readonly connecting: boolean;
  /** True briefly when a locked quick-win points the user at this card. */
  readonly pulsing: boolean;
  readonly onConnect: (toolkit: string) => void;
  /** When provided, a connected card shows a one-click "Disconnect" affordance. */
  readonly onDisconnect?: (toolkit: string) => void;
  readonly disconnecting?: boolean;
}

export const ConnectAppCard = forwardRef<HTMLDivElement, ConnectAppCardProps>(
  function ConnectAppCard(
    { model, connected, connecting, pulsing, onConnect, onDisconnect, disconnecting },
    ref,
  ) {
    const { t } = useI18n();
    const { toolkit, name, icon: Icon, brand, glyph } = model;
    const [logoFailed, setLogoFailed] = useState(false);
    return (
      <div
        ref={ref}
        className={cn(
          "connect-app-card w-full",
          pulsing && "ring-2 ring-primary ring-offset-2 ring-offset-background",
        )}
      >
        <div className="connect-app-card-inner items-center gap-3 p-2 pe-3">
          {logoFailed ? (
            <div
              className="flex size-10 shrink-0 items-center justify-center rounded-[11px]"
              style={{ backgroundColor: brand }}
            >
              <Icon className="size-6" style={{ color: glyph }} aria-hidden="true" />
            </div>
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-white/70 shadow-[inset_0_0_0_1px_rgba(200,230,255,0.4)] dark:bg-white/10">
              <img
                src={`https://logos.composio.dev/api/${toolkit}`}
                alt={t("connect.logoAlt", { name })}
                className="size-7 object-contain"
                loading="lazy"
                onError={() => setLogoFailed(true)}
              />
            </div>
          )}
          <span className="min-w-0 flex-1 truncate text-sm font-semibold tracking-[-0.02em] text-foreground">
            {name}
          </span>
          {connected ? (
            <div className="flex shrink-0 items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                <WorkStepLifecycleIndicator state="completed" size="sm" />
                {t("connect.connected")}
              </span>
              {onDisconnect ? (
                <button
                  type="button"
                  onClick={() => onDisconnect(toolkit)}
                  disabled={disconnecting}
                  aria-label={t("connect.disconnectApp", { name })}
                  title={t("connect.disconnect")}
                  className="inline-flex size-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <XIcon className="size-3.5" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onConnect(toolkit)}
              disabled={connecting}
              className="shrink-0 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              {connecting ? t("connect.connecting") : t("connect.connect")}
            </button>
          )}
        </div>
      </div>
    );
  },
);
